import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Send, FileDown, BookOpenCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

function formatEUR(v) {
  return Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR" });
}
function formatDateIT(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y}`;
}

export default function ContoSeparato() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selEntries, setSelEntries] = useState({});
  const [selVers, setSelVers] = useState({});
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [history, setHistory] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, h] = await Promise.all([
        api.get("/conto-separato/summary"),
        api.get("/conto-separato/distinte"),
      ]);
      setData(s.data);
      setHistory(h.data);
      setSelEntries({});
      setSelVers({});
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const totalSelEntries = useMemo(() => {
    if (!data) return 0;
    return data.entries
      .filter((e) => selEntries[e.id])
      .reduce((s, e) => s + Number(e.importo || 0), 0);
  }, [selEntries, data]);

  const totalSelVers = useMemo(() => {
    if (!data) return 0;
    return data.versamenti
      .filter((v) => selVers[v.id])
      .reduce((s, v) => s + Number(v.totale || 0), 0);
  }, [selVers, data]);

  const totalSelected = totalSelEntries + totalSelVers;
  const nSelEntries = useMemo(() => Object.values(selEntries).filter(Boolean).length, [selEntries]);
  const nSelVers = useMemo(() => Object.values(selVers).filter(Boolean).length, [selVers]);

  const toggleAllEntries = (checked) => {
    if (!data) return;
    const next = {};
    data.entries.forEach((e) => { next[e.id] = checked; });
    setSelEntries(next);
  };
  const toggleAllVers = (checked) => {
    if (!data) return;
    const next = {};
    data.versamenti.forEach((v) => { next[v.id] = checked; });
    setSelVers(next);
  };

  const submit = async () => {
    const entry_ids = Object.entries(selEntries).filter(([, v]) => v).map(([k]) => k);
    const versamento_ids = Object.entries(selVers).filter(([, v]) => v).map(([k]) => k);
    if (!entry_ids.length && !versamento_ids.length) {
      toast.error("Seleziona almeno un elemento");
      return;
    }
    if (!window.confirm(`Confermi l'emissione della distinta per ${formatEUR(totalSelected)}?`)) return;
    setSubmitting(true);
    try {
      await api.post("/conto-separato/distinta", { entry_ids, versamento_ids, note });
      toast.success("Distinta creata e inviata via email");
      setNote("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const downloadPdf = async (id, dataD) => {
    try {
      const res = await api.get(`/conto-separato/distinte/${id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `distinta_${dataD}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  if (loading) return <div className="text-slate-500 text-sm">Caricamento…</div>;

  const isEmpty = (!data || (data.entries.length === 0 && data.versamenti.length === 0));

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Conto separato</div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">Distinta versamenti in agenzia</h1>
          <p className="text-sm text-slate-500 mt-1">
            Riepilogo dei movimenti su conto separato: incassi non in contante/assegno e versamenti effettuati sul C/C agenzia. Seleziona le voci da includere nella distinta e generala in PDF.
          </p>
        </div>
        <Link to="/foglio-cassa">
          <Button variant="outline" data-testid="back-to-sheet">Torna al foglio cassa</Button>
        </Link>
      </div>

      {/* Summary tiles */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="p-4 border-slate-200" data-testid="cs-tile-incassi">
          <div className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Incassi su conto separato</div>
          <div className="font-heading text-2xl font-bold text-slate-900 font-mono-num">{formatEUR(data?.totale_incassi)}</div>
          <div className="text-xs text-slate-500 mt-1">{data?.entries?.length || 0} polizze aperte</div>
        </Card>
        <Card className="p-4 border-slate-200" data-testid="cs-tile-versamenti">
          <div className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Versamenti su C/C agenzia</div>
          <div className="font-heading text-2xl font-bold text-slate-900 font-mono-num">{formatEUR(data?.totale_versamenti)}</div>
          <div className="text-xs text-slate-500 mt-1">{data?.versamenti?.length || 0} versamenti aperti</div>
        </Card>
        <Card className="p-4 border-emerald-200 bg-emerald-50/40" data-testid="cs-tile-totale">
          <div className="text-xs text-emerald-700 uppercase font-semibold tracking-wider">Totale conto separato</div>
          <div className="font-heading text-2xl font-bold text-emerald-700 font-mono-num">{formatEUR(data?.totale)}</div>
          <div className="text-xs text-slate-500 mt-1">da includere nella distinta</div>
        </Card>
      </div>

      {isEmpty && (
        <Card className="p-12 text-center border-slate-200" data-testid="empty-conto-separato">
          <BookOpenCheck className="w-12 h-12 text-slate-300 mx-auto" />
          <h3 className="font-heading text-lg font-semibold text-slate-900 mt-4">Nessun movimento aperto sul conto separato</h3>
          <p className="text-sm text-slate-500 mt-1">
            Quando registrerai incassi non in contante/assegno o effettuerai versamenti su C/C agenzia, appariranno qui per essere inclusi nella prossima distinta.
          </p>
        </Card>
      )}

      {/* Incassi */}
      {data?.grouped_entries?.length > 0 && (
        <Card className="border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-4 py-3 flex items-center justify-between border-b border-slate-200">
            <div>
              <div className="font-heading font-semibold text-slate-900">Incassi su conto separato</div>
              <div className="text-xs text-slate-500">{data.entries.length} polizze · totale {formatEUR(data.totale_incassi)}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => toggleAllEntries(true)} data-testid="cs-select-all-entries">Seleziona tutto</Button>
          </div>
          {data.grouped_entries.map((g) => (
            <div key={g.date}>
              <div className="bg-slate-50/60 px-4 py-2 text-xs text-slate-600 font-semibold border-b border-slate-100">
                Giornata {formatDateIT(g.date)} — totale {formatEUR(g.total)}
              </div>
              <table className="w-full text-sm">
                <tbody className="divide-y divide-slate-100">
                  {g.entries.map((e) => (
                    <tr key={e.id} className={selEntries[e.id] ? "bg-blue-50/50" : "hover:bg-slate-50"} data-testid={`cs-entry-${e.id}`}>
                      <td className="px-4 py-3 w-10">
                        <Checkbox
                          checked={!!selEntries[e.id]}
                          onCheckedChange={() => setSelEntries((s) => ({ ...s, [e.id]: !s[e.id] }))}
                          data-testid={`cs-entry-cb-${e.id}`}
                        />
                      </td>
                      <td className="px-4 py-3 font-medium text-slate-900">
                        {e.contraente}
                        {e.urgente && <AlertTriangle className="w-3.5 h-3.5 text-red-600 inline-block ml-2" />}
                      </td>
                      <td className="px-4 py-3 text-slate-700 font-mono text-xs">{e.numero_polizza}</td>
                      <td className="px-4 py-3 text-slate-700">{e.metodo_pagamento}</td>
                      <td className="px-4 py-3 text-right font-mono-num font-semibold">{formatEUR(e.importo)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </Card>
      )}

      {/* Versamenti su C/C */}
      {data?.versamenti?.length > 0 && (
        <Card className="border-slate-200 overflow-hidden">
          <div className="bg-slate-50 px-4 py-3 flex items-center justify-between border-b border-slate-200">
            <div>
              <div className="font-heading font-semibold text-slate-900">Versamenti su C/C agenzia</div>
              <div className="text-xs text-slate-500">{data.versamenti.length} versamenti · totale {formatEUR(data.totale_versamenti)}</div>
            </div>
            <Button size="sm" variant="outline" onClick={() => toggleAllVers(true)} data-testid="cs-select-all-vers">Seleziona tutto</Button>
          </div>
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-left">
                <th className="px-4 py-2 w-10"></th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Data</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Polizze</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Totale</th>
                <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Note</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data.versamenti.map((v) => (
                <tr key={v.id} className={selVers[v.id] ? "bg-blue-50/50" : "hover:bg-slate-50"} data-testid={`cs-vers-${v.id}`}>
                  <td className="px-4 py-3">
                    <Checkbox
                      checked={!!selVers[v.id]}
                      onCheckedChange={() => setSelVers((s) => ({ ...s, [v.id]: !s[v.id] }))}
                      data-testid={`cs-vers-cb-${v.id}`}
                    />
                  </td>
                  <td className="px-4 py-3 font-medium">{formatDateIT(v.data_versamento)}</td>
                  <td className="px-4 py-3 text-right font-mono-num">{v.n_polizze}</td>
                  <td className="px-4 py-3 text-right font-mono-num font-semibold">{formatEUR(v.totale)}</td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{v.note || ""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Action bar */}
      {!isEmpty && (
        <Card className="p-5 border-slate-200 sticky bottom-4 card-elevated">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="text-xs text-slate-500 uppercase font-semibold tracking-wider">Selezionato</div>
                <div className="font-heading text-xl font-bold text-slate-900">
                  {nSelEntries} polizze + {nSelVers} versamenti · <span className="text-emerald-700">{formatEUR(totalSelected)}</span>
                </div>
              </div>
              <div className="flex-1 md:max-w-xs">
                <Textarea placeholder="Note (facoltative)" value={note} onChange={(e) => setNote(e.target.value)} rows={2} data-testid="cs-note" />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={submit}
                disabled={submitting || (nSelEntries + nSelVers) === 0}
                size="lg"
                className="bg-slate-900 hover:bg-slate-800 text-white"
                data-testid="cs-submit"
              >
                <Send className="w-4 h-4 mr-2" />
                {submitting ? "Generazione in corso…" : "Genera distinta e invia email"}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {/* History */}
      <div>
        <h2 className="font-heading text-xl font-semibold text-slate-900 mt-8 mb-3">Distinte emesse</h2>
        {history.length === 0 ? (
          <p className="text-sm text-slate-500">Nessuna distinta ancora emessa.</p>
        ) : (
          <Card className="border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-slate-50 text-left">
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Data</th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Incassi</th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Versamenti</th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Totale</th>
                  <th className="px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Allegato</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {history.map((d) => (
                  <tr key={d.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 font-medium">{formatDateIT(d.data_distinta)}</td>
                    <td className="px-4 py-3 text-right font-mono-num">{d.n_incassi} · {formatEUR(d.totale_incassi)}</td>
                    <td className="px-4 py-3 text-right font-mono-num">{d.n_versamenti} · {formatEUR(d.totale_versamenti)}</td>
                    <td className="px-4 py-3 text-right font-mono-num font-semibold">{formatEUR(d.totale)}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="outline" onClick={() => downloadPdf(d.id, d.data_distinta)} data-testid={`cs-download-${d.id}`}>
                        <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </div>
    </div>
  );
}
